import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AvatarPair } from "./Avatar.js";
import { formatScoreline, sideOf } from "../lib/matches.js";
import { MATCH_CATEGORY_LABEL, type MatchSummary } from "../lib/types.js";

/**
 * One match, as a scoresheet: the winners on the top line with a marker, the beaten side
 * beneath them, and one column per game so a three-game scoreline reads down as well as
 * across. Each side's number is in full ink on the games it took.
 *
 * Winner on top rather than "you" on top: the log is read by people who did not play in
 * most of it, and a sheet that reorders itself around the reader is harder to scan. Your own
 * side carries a YOU tag instead.
 *
 * Every name is a link to that player. This is the main way anyone finds a profile: you
 * read a scoreline, wonder who beat them, and tap.
 *
 * `framed` is the confirmation queue's version: a ruled box with an orange strip saying
 * who logged it, because that is the one place a row is asking you to do something.
 */
export function MatchRow({
  match,
  memberId,
  showEvent = true,
  framed = false,
  showWhen = true,
  action,
  children,
}: {
  match: MatchSummary;
  /**
   * Whoever is reading — their side gets the YOU tag. Always the viewer, never the person a
   * list is about: a profile of somebody else passing their id here tagged *them* as you.
   */
  memberId?: string;
  /** Off on an event's own results screen, where saying it four times is noise. */
  showEvent?: boolean;
  framed?: boolean;
  /** Off under a day heading, which already says when. */
  showWhen?: boolean;
  /** A small control at the end of the status line — an organiser's "Fix score" — so it
   * rides along with the row instead of taking a line of its own under every match. */
  action?: ReactNode;
  children?: ReactNode;
}) {
  const loser = match.winner === "A" ? "B" : "A";
  const loggedBy = match.participants.find((p) => p.memberId === match.enteredBy);

  return (
    <article
      className={framed ? "border-2" : "border-b py-3"}
      style={{ borderColor: framed ? "var(--color-line)" : "var(--color-line-muted)" }}
    >
      {framed ? (
        <div
          className="font-mono flex min-h-[28px] items-center justify-between gap-3 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em]"
          style={{ background: "var(--color-attention-fill)", color: "var(--color-ink)" }}
        >
          <span>Confirm this</span>
          <span className="truncate">
            {loggedBy ? `Logged by ${firstName(loggedBy.displayName)} · ` : ""}
            {relative(match.playedAt)}
          </span>
        </div>
      ) : null}

      <div className={framed ? "px-3 pt-3" : ""}>
        <div className="flex flex-col gap-1.5">
          <SheetLine match={match} side={match.winner} won memberId={memberId} />
          <SheetLine match={match} side={loser} won={false} memberId={memberId} />
        </div>

        <div className="font-mono mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 pl-[18px] text-[10px] font-semibold uppercase tracking-[0.1em]">
          {framed ? null : <Status status={match.status} />}
          <span className="font-medium text-ink-muted">
            {match.category ? `${MATCH_CATEGORY_LABEL[match.category]} ` : ""}
            {match.kind}
            {framed || !showWhen ? "" : ` · ${relative(match.playedAt)}`}
            {/* Where it was played, when that is anywhere. A tournament match is otherwise
                indistinguishable from a knockabout on a Tuesday, and the difference is the
                reason half of these were played at all. */}
            {showEvent && match.event ? (
              <>
                {" · "}
                <Link to={`/events/${match.event.id}`} className="underline">
                  {match.event.name}
                </Link>
              </>
            ) : null}
            {/* Said on the row rather than only on the log screen, because the person
                reading this is often not the person who logged it — and a confirmed match
                that moved nobody's rating looks like a bug unless the row says otherwise. */}
            {!match.movesRatings && match.status !== "void" ? " · unrated" : ""}
          </span>
          {action ? <span className="ml-auto">{action}</span> : null}
        </div>
      </div>

      {children ? <div className={framed ? "px-3 pb-3" : ""}>{children}</div> : null}
    </article>
  );
}

/** One side's line on the sheet: marker, faces, names, then a column per game. */
function SheetLine({
  match,
  side,
  won,
  memberId,
}: {
  match: MatchSummary;
  side: "A" | "B";
  won: boolean;
  memberId?: string;
}) {
  const people = match.participants.filter((p) => p.side === side);
  const you = memberId !== undefined && people.some((p) => p.memberId === memberId);

  return (
    <div
      className="flex items-center gap-2"
      style={won ? undefined : { color: "var(--color-ink-muted)" }}
    >
      <span className="w-2.5 shrink-0 text-[10px]" style={{ color: "var(--color-ink)" }}>
        {won ? (
          <>
            <span aria-hidden>▶</span>
            <span className="sr-only">Won:</span>
          </>
        ) : (
          <span className="sr-only">Lost:</span>
        )}
      </span>
      <AvatarPair people={sideOf(match, side)} size={24} />
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className={`truncate text-[15px] ${won ? "font-bold" : ""}`}>
          {people.map((person, i) => (
            <span key={person.memberId}>
              {i > 0 ? " & " : ""}
              <Link to={`/player/${person.memberId}`} className="hover:underline">
                {person.displayName}
              </Link>
            </span>
          ))}
        </span>
        {you ? (
          <span
            className="font-mono shrink-0 border-[1.5px] px-1 text-[9px] font-semibold tracking-[0.12em]"
            style={{ borderColor: "currentColor" }}
          >
            YOU
          </span>
        ) : null}
      </span>
      <span className="font-mono flex shrink-0 text-base">
        <span className="sr-only">{formatScoreline(match.games, side)}</span>
        {match.games.map((game, i) => {
          const mine = side === "A" ? game.a : game.b;
          const theirs = side === "A" ? game.b : game.a;
          return (
            <span
              key={i}
              aria-hidden
              className="w-[30px] text-right"
              style={
                mine > theirs
                  ? { color: "var(--color-ink)", fontWeight: 600 }
                  : { color: "var(--color-ink-muted)", fontWeight: 400 }
              }
            >
              {mine}
            </span>
          );
        })}
      </span>
    </div>
  );
}

/** The row's state as a stamp. Confirmed is the quiet default; only disputed is orange. */
function Status({ status }: { status: MatchSummary["status"] }) {
  const stamp = {
    confirmed: { label: "Confirmed", border: "1.5px solid var(--color-line)", color: undefined },
    pending: {
      label: "Awaiting",
      border: "1.5px dashed var(--color-ink-muted)",
      color: "var(--color-ink-muted)",
    },
    disputed: {
      label: "Disputed",
      border: "1.5px solid var(--color-attention)",
      color: "var(--color-attention)",
    },
    void: {
      label: "Cancelled",
      border: "1.5px dashed var(--color-ink-muted)",
      color: "var(--color-ink-muted)",
    },
  }[status];

  return (
    <span className="px-1.5 py-px" style={{ border: stamp.border, color: stamp.color }}>
      {stamp.label}
    </span>
  );
}

const firstName = (name: string) => name.split(" ")[0] ?? name;

/** "Today", "Yesterday", or "Sun 28 Sep" — the heading a day's matches sit under. */
export function dayLabel(iso: string): string {
  const day = new Date(iso);
  const today = new Date();
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(today) - startOf(day)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return day.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(day.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}

export function relative(iso: string): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (hours < 1) return "just now";
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
